import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { analyzePatterns, getPriorityInterventions, generateActionPlan, ACTION_TYPES } from '../data/aiRecommendationLogic';
import { departmentData } from '../data/departmentRiskLogic';

export const AIActionEngine = () => {
  const [selectedDeptId, setSelectedDeptId] = useState(departmentData[0].id);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);

  const selectedDept = useMemo(() => departmentData.find(d => d.id === selectedDeptId), [selectedDeptId]);

  const runAnalysis = () => {
    setIsAnalyzing(true);
    setResults(null);
    
    // Simulerer AI-prosessering
    setTimeout(() => {
      const interventions = getPriorityInterventions(selectedDept.metrics);
      const patterns = analyzePatterns(selectedDept.metrics);
      const plan = generateActionPlan(selectedDept.name, interventions);
      
      setResults({ interventions, patterns, plan });
      setIsAnalyzing(false);
    }, 1500);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI Action Recommendation Engine</h1>
          <p className="text-slate-500 mt-1">Datadrevet tiltaksplanlegging basert på organisasjonens helsetilstand.</p>
        </div>
        <div className="flex gap-2">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20"
            value={selectedDeptId}
            onChange={(e) => {
              setSelectedDeptId(e.target.value);
              setResults(null);
            }}
          >
            {departmentData.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Button variant="primary" onClick={runAnalysis} disabled={isAnalyzing} icon={<SafeIcon name="Zap" />}>
            {isAnalyzing ? 'Analyserer...' : 'Kjør AI-Analyse'}
          </Button>
        </div>
      </div>

      {!results && !isAnalyzing && (
        <div className="py-20 text-center bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200">
          <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
            <SafeIcon name="Cpu" className="text-4xl text-slate-300" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Klar til å generere tiltak</h3>
          <p className="text-slate-500 max-w-xs mx-auto text-sm mt-2">
            Velg en avdeling og trykk på knappen for å la AI-motoren foreslå en prioritert handlingsplan.
          </p>
        </div>
      )}

      {isAnalyzing && (
        <div className="py-20 text-center space-y-4">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-500 font-black uppercase tracking-widest text-[10px] animate-pulse">
            Mapper mønstre og genererer tiltaksplan...
          </p>
        </div>
      )}

      <AnimatePresence>
        {results && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Main Action Plan */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-none shadow-xl">
                <CardHeader title="Priority Interventions" subtitle={`Tiltak med høyest effekt for ${selectedDept.name}`} />
                <CardContent className="space-y-4">
                  {results.interventions.map((action, i) => (
                    <div key={i} className="flex gap-6 p-6 bg-slate-50 rounded-[32px] border border-slate-100 hover:bg-white hover:shadow-lg transition-all items-center group">
                      <div className={`w-14 h-14 rounded-2xl bg-${action.color}-500/10 text-${action.color}-600 flex items-center justify-center shrink-0`}>
                        <SafeIcon name={action.icon} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <h4 className="font-bold text-slate-900">{action.label}</h4>
                          <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                            action.priority === 'Critical' ? 'bg-rose-500 text-white' : 'bg-indigo-600 text-white'
                          }`}>
                            {action.priority}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed">{action.reason}</p>
                      </div>
                      <Button variant="ghost" className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <SafeIcon name="ArrowRight" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Action Plan Timeline */}
              <Card className="border-l-4 border-l-slate-900">
                <CardHeader title="Department Action Plan" subtitle="Foreslått tidslinje for implementering" icon={<SafeIcon name="Calendar" />} />
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {results.plan.timeline.map((item, i) => (
                      <div key={i} className="p-6 flex gap-8 items-start hover:bg-slate-50 transition-colors">
                        <div className="text-right w-20 shrink-0">
                          <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{item.week}</p>
                        </div>
                        <div className="space-y-2">
                          <h5 className="font-bold text-slate-900 text-sm">{item.title}</h5>
                          <div className="flex flex-wrap gap-2">
                            {item.actions.length > 0 ? item.actions.map((act, j) => (
                              <span key={j} className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-600">
                                {act}
                              </span>
                            )) : <span className="text-[10px] italic text-slate-400">Ingen spesifikke tiltak planlagt</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar: AI Analysis & Workshops */}
            <div className="space-y-6">
              <Card className="bg-slate-900 border-none text-white">
                <CardHeader title="AI Pattern Analysis" icon={<SafeIcon name="Cpu" className="text-indigo-400" />} />
                <CardContent className="space-y-4">
                  {results.patterns.map((p, i) => (
                    <div key={i} className="p-4 bg-white/5 rounded-2xl border border-white/5">
                      <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1">{p.challenge}</p>
                      <h5 className="font-bold text-white text-sm">{p.title}</h5>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{p.desc}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader title="Recommended Workshops" icon={<SafeIcon name="Users" className="text-indigo-600" />} />
                <CardContent className="space-y-3">
                  {results.interventions.filter(a => a.label.includes('Workshop')).map((ws, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between group cursor-pointer hover:bg-slate-100">
                      <span className="text-xs font-bold text-slate-700">{ws.label}</span>
                      <SafeIcon name="ChevronRight" className="text-slate-300 group-hover:text-indigo-600" />
                    </div>
                  ))}
                  <Button variant="secondary" className="w-full mt-2">Bla i Workshop-bibliotek</Button>
                </CardContent>
              </Card>

              <div className="bg-emerald-600 p-8 rounded-[40px] text-white shadow-xl shadow-emerald-100">
                <SafeIcon name="TrendingUp" className="text-4xl text-emerald-200 mb-6" />
                <h4 className="text-xl font-black mb-2 uppercase tracking-tighter">Expected Impact</h4>
                <p className="text-xs text-emerald-100 leading-relaxed mb-6">
                  Ved full implementering av denne planen forventer vi en <b>15-20% forbedring</b> i Psychological Safety innen 12 uker.
                </p>
                <Button variant="secondary" className="w-full bg-white text-emerald-600 border-none">Last ned Simuleringsrapport</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};